import {
  ChangeDetectionStrategy,
  Component,
  inject,
  NgZone,
  Renderer2,
  signal,
  WritableSignal,
} from '@angular/core';

import {
  debounceTime,
  fromEvent,
  map,
  startWith,
  Subscription,
  switchMap,
  takeUntil,
  throttleTime,
} from 'rxjs';

import { SocialNetworks } from '@shared/models';

const MAX_OUTER_LINE_DISTANCE = 30;
const MAX_ICON_DISTANCE = 10;

@Component({
  selector: 'rp-home-socials',
  standalone: true,
  templateUrl: './socials.component.html',
  styleUrl: './socials.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialsComponent {
  readonly socialNetworks = SocialNetworks;
  readonly socialActiveIndex: WritableSignal<number | null> = signal<number | null>(null);

  private readonly _ngZone = inject(NgZone);
  private readonly _renderer = inject(Renderer2);

  private _mouseEnterSubscription = Subscription.EMPTY;

  onMouseEnter(index: number): void {
    this.socialActiveIndex.set(index);
  }

  onMouseLeave(index: number): void {
    if (this.socialActiveIndex() === index) {
      this.socialActiveIndex.set(null);
    }

    this._mouseEnterSubscription.unsubscribe();
  }

  onMouseMove(index: number): void {
    const socialContainer: HTMLElement = this._getQueryByIndex('.socials__social', index);
    const socialLink: HTMLElement = this._getQueryByIndex('.socials__social-link', index);
    const socialIcon: HTMLElement = this._getQueryByIndex('.socials__social-icon', index);

    this._ngZone.runOutsideAngular(() => {
      const mouseLeave$ = fromEvent<MouseEvent>(socialContainer, 'mouseleave');
      const mouseEnter$ = fromEvent<MouseEvent>(socialContainer, 'mouseenter').pipe(
        startWith(null),
      );
      const mouseMove$ = fromEvent<MouseEvent>(socialContainer, 'mousemove').pipe(
        throttleTime(100),
        map((event: MouseEvent) => {
          const rect = socialContainer.getBoundingClientRect();
          const x = event.clientX - rect.left - rect.width / 2;
          const y = event.clientY - rect.top - rect.height / 2;

          return { x, y };
        }),
      );

      this._mouseEnterSubscription = mouseEnter$
        .pipe(switchMap(() => mouseMove$.pipe(debounceTime(1), takeUntil(mouseLeave$))))
        .subscribe(({ x, y }) => {
          this._renderer.setStyle(socialLink, 'display', 'flex');

          const xLinkPosition = Math.abs(x) > MAX_OUTER_LINE_DISTANCE ? MAX_OUTER_LINE_DISTANCE : x;
          const yLinkPosition = Math.abs(y) > MAX_OUTER_LINE_DISTANCE ? MAX_OUTER_LINE_DISTANCE : y;
          const xIconPosition = Math.abs(x) > MAX_ICON_DISTANCE ? MAX_ICON_DISTANCE : x;
          const yIconPosition = Math.abs(y) > MAX_ICON_DISTANCE ? MAX_ICON_DISTANCE : y;

          this._renderer.setStyle(
            socialLink,
            'transform',
            `translate3d(${xLinkPosition}px, ${yLinkPosition}px, 0) scale3d(1.2, 1.2, 1)`,
          );

          this._renderer.setStyle(
            socialIcon,
            'transform',
            `translate3d(${xIconPosition}px, ${yIconPosition}px, 0) scale3d(1.5, 1.5, 1)`,
          );
        });

      mouseLeave$.subscribe(() => {
        this._renderer.setStyle(socialLink, 'transform', `translate3d(0, 0, 0) scale3d(0, 0, 1)`);
        this._renderer.setStyle(socialIcon, 'transform', `translate3d(0, 0, 0) scale3d(1, 1, 1)`);
      });
    });
  }

  private _getQueryByIndex(ref: string, index: number): HTMLElement {
    return document.querySelectorAll<HTMLElement>(ref)[index];
  }
}
